import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LayoutContext } from '../../contexts/layout.context';
import { authSelectors } from '../../store/selectors/auth';
import { getUserInfoThunk } from '../../store/thunks/profile';
import { getPayActualCountTariffThunk, getPayActualCountThunk, getPayThunk } from '../../store/thunks/finances';
import { financesSelectors } from '../../store/selectors/finances';
import actions from '../../store/actions';
import { copyTextToClipboard } from '../../utils/copy';
import Spinner from '../../common/Spinner/Spinner';
import Head from '../../components/Head/Head';
import FinancePayTable from '../../components/FinancePay/FinancePayTable/FinancePayTable';
import FinancePayCard from '../../components/FinancePay/FinancePayCard/FinancePayCard';
import SelectEl from '../../components/SelectEl/SelectEl';
import FinancesPayBtns from '../../components/FinancePay/FinancesPayBtns/FinancesPayBtns';
import ModalAgreement from '../../components/Modals/ModalAgreement';
import Modal from '../../components/FinancePay/ModalFinances/Modal';
import Plug from '../../components/plug/plug.component';
import sprite from '../../img/sprites.svg';
import PayV2 from '../../components/FinancesV2/PayV2';
import usePreviewFeature from '../../hooks/usePreviewFeature';
import { DEMO_FULL_COURSE, DEMO_INVOICE, DEMO_TARIFFS } from '../../utils/financeDemo';

// ---------- helpers for timer ----------
const parseInvoiceDate = (val) => {
  if (!val) return null;
  const str = String(val).trim();

  // yyyy-mm-dd... (ISO / mysql)
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const d = new Date(str.replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
  }

  // dd.mm.yyyy HH:ii[:ss]
  const m = str.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    const dd = Number(m[1]);
    const mm = Number(m[2]) - 1;
    const yyyy = Number(m[3]);
    const HH = Number(m[4] ?? 0);
    const ii = Number(m[5] ?? 0);
    const ss = Number(m[6] ?? 0);
    return new Date(yyyy, mm, dd, HH, ii, ss);
  }

  return null;
};

const formatCountdown = (totalSeconds) => {
  const s = Math.max(0, totalSeconds);
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};
// -------------------------------------

const FinancePay = () => {
  const { setLayout } = useContext(LayoutContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedTariff, setSelectedTariff] = useState(0);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [openModal, setOpenModal] = useState({
    open: false,
    url: null,
    name: null,
    method: null,
    appointment: null,
  });
  const [allowPay, setAllowPay] = useState(false);
  const [openModalMessage, setOpenModalMessage] = useState(false);
  const [invoiceArr, setInvoiceArr] = useState(null);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [demoHours, setDemoHours] = useState(0);
  const isV2 = usePreviewFeature('cabinet-v2');

  const finances = useSelector(financesSelectors);
  const auth = useSelector(authSelectors);

  const token = auth.token;
  const load = finances.load;
  const invoice = finances.invoice;
  const tariffs = finances.tariffs;
  const methods = finances.methods;
  const notFound = finances.notFound;
  const paramInvoiceStore = finances.paramInvoice;
  const paramPaymentUuidStore = finances.paramPaymentUuid;
  const error = finances.error;
  const loadBtn = finances.loadBtn;
  const message = finances.message;
  const fullCourse = finances.fullCourse;

  const productId = JSON.parse(localStorage.getItem('product_id'));
  const invoiceId = JSON.parse(localStorage.getItem('invoice_id'));
  const isDemo = invoiceId === 'demo-actual';
  const user = JSON.parse(localStorage.getItem('user'));

  const params = new URLSearchParams(location.search);
  const paramInvoice = params.get('invoice');
  const paramPaymentUuid = params.get('payment_uuid');

  const bonusBanner = invoice?.fast_payment_bonus_banner || null;

  const bonusTimerDeadline = useMemo(() => {
    if (!bonusBanner?.show || !bonusBanner?.timer_deadline_at) return null;
    return parseInvoiceDate(bonusBanner.timer_deadline_at);
  }, [bonusBanner]);

  const showBonusTimer = useMemo(() => {
    if (!bonusBanner?.show || !bonusTimerDeadline) return false;
    return Date.now() < bonusTimerDeadline.getTime();
  }, [bonusBanner, bonusTimerDeadline]);

  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!showBonusTimer || !bonusTimerDeadline) {
      setSecondsLeft(0);
      return;
    }

    const tick = () => {
      const left = Math.ceil((bonusTimerDeadline.getTime() - Date.now()) / 1000);
      setSecondsLeft(Math.max(0, left));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [showBonusTimer, bonusTimerDeadline]);

  // another user
  useEffect(() => {
    if (paramInvoice) {
      dispatch(actions.finances.setParamInvoice(paramInvoice));
    }

    if (paramPaymentUuid) {
      dispatch(actions.finances.setParamPaymentUuid(paramPaymentUuid));
    }
  }, [paramInvoice, paramPaymentUuid]);

  useEffect(() => {
    if (paramInvoiceStore && paramPaymentUuidStore) {
      const data = { invoice_id: paramInvoiceStore, data: { payment_uuid: paramPaymentUuidStore } };
      dispatch(getPayActualCountThunk(data));
    }

    if (paramInvoiceStore && !paramPaymentUuidStore && user?.id) {
      const data = { invoice_id: paramInvoiceStore, data: { user_id: user?.id } };
      dispatch(getPayActualCountThunk(data));
    }
  }, [paramInvoiceStore, paramPaymentUuidStore]);

  useEffect(() => {
    if (notFound) {
      navigate('/');
    }
    if (notFound && !token) {
      navigate('/login');
    }
  }, [notFound]);

  useEffect(() => {
    if (token) {
      dispatch(getUserInfoThunk({ token }));
    }
  }, [token]);

  useEffect(() => {
    document.title = 'Рахунки | New Brain';
    setLayout({ page: 'Рахунки' });
  }, [setLayout]);

  useEffect(() => {
    if (message?.length > 0) {
      setOpenModalMessage(true);
    }
  }, [message]);

  useEffect(() => {
    if (!tariffs) {
      setOptions([]);
      return;
    }

    const opts = tariffs.map((tariff) => {
      const hours = Number(tariff.hours) || 0;
      const showHours = Number(tariff.show_hours) || 0;
      const bonus = Number(tariff.bonus_hours) || 0;

      const label =
        String(tariff.is_special) === '1' || tariff.is_special === 1
          ? `${tariff.name} (${showHours}${bonus > 0 ? ` + ${bonus} = ${hours}` : ''} занять, за ${tariff.total_price} грн)`
          : `${tariff.name} (${hours} занять, ${tariff.price} грн за заняття, ${tariff.total_price} грн)`;

      return {
        value: hours,
        label,
      };
    });

    setOptions(opts);
  }, [tariffs]);

  useEffect(() => {
    setSelectedOption(null);

    if (options.length > 0 && invoice) {
      if (invoice.tariff_id) {
        setSelectedOption(options.find((option) => option.value === invoice.tariff_id) || null);
      }
    }

    return () => setSelectedOption(null);
  }, [options, invoice]);

  useEffect(() => {
    if (!invoiceId && !paramInvoiceStore) {
      navigate('/finance/accounts');
    }
    dispatch(actions.finances.getFinancesError(''));
  }, []);

  useEffect(() => {
    if (invoiceId && user?.id && !paramInvoice && !paramInvoiceStore && !isDemo) {
      const data = { invoice_id: invoiceId, data: { user_id: user?.id } };
      dispatch(getPayActualCountThunk(data));
    }
  }, [invoiceId]);

  useEffect(() => {
    if (productId && invoiceId && token && selectedTariff > 0 && !paramInvoiceStore) {
      const data = {
        invoice_id: invoiceId,
        tariff_id: selectedTariff,
        token,
      };
      dispatch(getPayActualCountTariffThunk(data));
      setSelectedTariff(0);
    }

    if (paramInvoiceStore && selectedTariff > 0) {
      const data = {
        invoice_id: paramInvoiceStore,
        tariff_id: selectedTariff,
      };
      dispatch(getPayActualCountTariffThunk(data));
      setSelectedTariff(0);
    }
  }, [selectedTariff]);

  useEffect(() => {
    if (invoice) {
      setInvoiceArr(Object.keys(invoice));
    }
  }, [invoice]);

  const setLink = (document_url) => {
    window.open(document_url, '');
  };

  useEffect(() => {
    if (allowPay) {
      setAllowPay(false);
      handleMethodPay(openModal.method, openModal.appointment);
    }
  }, [allowPay]);

  const handleMethodPay = (method, appointment) => {
    document.body.classList.remove('open');

    if (method.type === 'details') {
      localStorage.setItem('url', JSON.stringify(method.pay_link));
      return navigate('/finance/accounts/pay/scores');
    }

    if (method.type === 'privat24') {
      copyTextToClipboard(appointment);
      localStorage.setItem('url', JSON.stringify(method.pay_link));
      return navigate('/finance/accounts/pay/message');
    }

    return setPayment(method.pay_link);
  };

  const setPayment = (url) => {
    if (url && !loadingBtn) {
      dispatch(getPayThunk(url));
      setLoadingBtn(true);
    }
  };

  const goBack = () => {
    localStorage.removeItem('invoice_id');
    navigate('/finance/accounts');
  };

  if (isV2 && (isDemo || invoice?.entity_type === 'student')) {
    const v2Invoice = isDemo ? DEMO_INVOICE : invoice;
    const v2Tariffs = isDemo ? DEMO_TARIFFS : (tariffs || []);

    return (
      <div className='finances-pay container finances--v2'>
        <ModalAgreement openModal={openModal} setAllowPay={setAllowPay} setOpenModal={setOpenModal} type="pay" loadBtn={loadBtn} />
        <Modal openModal={openModalMessage} setOpenModalMessage={setOpenModalMessage} message={message} token={token} />

        <Head
          textHead="Оплата"
          subtitle={v2Invoice?.customer_counteragent_full_name && `за ${v2Invoice.customer_counteragent_full_name}`}
          goBack={goBack}
          blockBtn={token ? false : true}
        />

        {load && !isDemo ? <Spinner /> : (
          v2Tariffs.length > 0 ? (
            <PayV2
              invoice={v2Invoice}
              tariffs={v2Tariffs}
              fullCourse={isDemo ? DEMO_FULL_COURSE : fullCourse}
              selectedHours={isDemo ? demoHours : Number(invoice?.tariff_id) || 0}
              onSelect={isDemo ? setDemoHours : setSelectedTariff}
              methods={methods || []}
              setOpenModal={setOpenModal}
              loadingBtn={loadingBtn}
              setLoadingBtn={setLoadingBtn}
              setLink={setLink}
              isDemo={isDemo}
              notice={bonusBanner?.show && showBonusTimer ? (
                <div className="pv2-bonus" role="status">
                  {bonusBanner?.title}
                  {bonusBanner?.text && <div className="pv2-bonus__text">{bonusBanner.text}</div>}
                  <div className="pv2-bonus__timer">Залишилось: {formatCountdown(secondsLeft)}</div>
                </div>
              ) : null}
            />
          ) : <Plug plug='Рахунок на оплату недоступний!' icon='file-closed' />
        )}
        {error?.tariff_id && <span className='error-message'>{error.tariff_id}</span>}
      </div>
    );
  }

  return (
    <div className='finances-pay container'>
      <ModalAgreement openModal={openModal} setAllowPay={setAllowPay} setOpenModal={setOpenModal} type="pay" loadBtn={loadBtn} />
      <Modal openModal={openModalMessage} setOpenModalMessage={setOpenModalMessage} message={message} token={token} />

      <Head
        textHead="Оплата"
        subtitle={invoice?.counteragent_full_name && `за ${invoice?.customer_counteragent_full_name}`}
        goBack={goBack}
        blockBtn={token ? false : true}
      />

      {load ? (
        <Spinner />
      ) : (
        <>
          {invoiceArr?.length > 0 && tariffs?.length > 0 ? (
            <>
              <div className="finances-pay__cards cards">
                <FinancePayCard setLink={setLink} invoice={invoice} />
              </div>

              <div className="finances-pay__table table">
                <div className="finances-pay__table-wrapper">
                  <FinancePayTable setLink={setLink} invoice={invoice} />
                </div>
              </div>

              {invoice?.counteragent_full_name && (
                <div className='finances-pay-box'>
                  <div className='finances-pay-box__title'>Замовник:</div>
                  <div className='finances-pay-box__desc'>{invoice.counteragent_full_name}</div>
                </div>
              )}

              {invoice?.customer_counteragent_full_name && (
                <div className='finances-pay-box'>
                  <div className='finances-pay-box__title'>Отримувач послуг:</div>
                  <div className='finances-pay-box__desc'>{invoice.customer_counteragent_full_name}</div>
                </div>
              )}

              {invoice?.document_url && (
                <div className='finances-pay-box'>
                  <div className="finances-pay-box__title">Переглянути:</div>
                  <div className="finances-pay-box__desc">
                    <svg className="icon"><use href={`${sprite}#doc`}></use></svg>
                    <button
                      onClick={() => setLink(invoice.document_url)}
                      className="table__link link link--violet link--lg finances-pay__link"
                    >
                      Рахунок-договір
                    </button>
                  </div>
                </div>
              )}

              <div className="finances-pay__select">
                <SelectEl
                  options={options}
                  setSelected={setSelectedTariff}
                  selectedOption={selectedOption}
                  placeholder="Оберіть тариф"
                />
                {error?.tariff_id && <span className='error-message'>{error.tariff_id}</span>}
              </div>

              {bonusBanner?.show && showBonusTimer && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid rgba(255,0,0,.35)',
                    color: '#d40000',
                    fontWeight: 600,
                    background: 'rgba(255,0,0,.06)',
                  }}
                >
                  {bonusBanner?.title}
                  {bonusBanner?.text && (
                    <div style={{ marginTop: 6, fontSize: 14, fontWeight: 500 }}>
                      {bonusBanner.text}
                    </div>
                  )}
                  <div style={{ marginTop: 6, fontSize: 18 }}>
                    Залишилось: {formatCountdown(secondsLeft)}
                  </div>
                </div>
              )}

              {invoice?.tariff_id > 0 && (
                <div className='finances-pay__btns pay-btns'>
                  {methods.map((method) => (
                    <FinancesPayBtns
                      key={method.type}
                      method={method}
                      setOpenModal={setOpenModal}
                      loadingBtn={loadingBtn}
                      setLoadingBtn={setLoadingBtn}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <Plug plug='Рахунок на оплату недоступний!' icon='file-closed' />
          )}
        </>
      )}
    </div>
  );
};

export default FinancePay;